import { useState } from 'react'
import { Card, CardTitle, CardDescription, SectionHeader, Button, Input, Badge } from '@/components/ui'
import { useApps } from '@/hooks/useApps'
import { useHostingSites, useRefreshHostingSites } from '@/hooks/useHostingSites'
import { useScanWorkspace } from '@/hooks/useFunctions'
import { useAuth } from '@/lib/auth'
import { ALLOWED_EMAILS } from '@/lib/firebase'
import { APP_VERSION, GIT_SHA, BUILD_TIME } from '@/lib/version'
import { RefreshCw, FolderSearch, Database, ShieldCheck, ExternalLink } from 'lucide-react'

export function SettingsPage() {
  const { user } = useAuth()
  const { data: apps } = useApps()
  const { data: sites } = useHostingSites()
  const refreshHosting = useRefreshHostingSites()
  const scanWorkspace = useScanWorkspace()
  const [scanPath, setScanPath] = useState("/Users/dr-goodluck/Desktop/Desktop - Dr G's M1 Max (2)/Antigravity Work Folder")

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Settings"
        title="Operator preferences"
        description="Auth status, sync triggers, build info."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="flex gap-3 items-start">
            <ShieldCheck size={20} className="text-success shrink-0 mt-0.5" />
            <div className="flex-1">
              <CardTitle className="!text-lg">Authenticated</CardTitle>
              <CardDescription className="mt-1">{user?.email}</CardDescription>
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/45 mt-3">
                Allowlist · {ALLOWED_EMAILS.join(' · ')}
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex gap-3 items-start">
            <Database size={20} className="text-gold-soft shrink-0 mt-0.5" />
            <div className="flex-1">
              <CardTitle className="!text-lg">Firestore</CardTitle>
              <CardDescription className="mt-1">
                Database <span className="font-mono text-bronze">acquisition-prep</span> on <span className="font-mono text-bronze">ascendant-road-holdings-llc</span>
              </CardDescription>
              <div className="flex items-center gap-3 mt-3">
                <Badge tone="cream">{apps?.length ?? 0} apps</Badge>
                <Badge tone="cream">{sites?.length ?? 0} sites</Badge>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <CardTitle>Sync actions</CardTitle>
        <CardDescription className="mt-1">Manual triggers for the AI-powered ingestion pipeline.</CardDescription>
        <div className="space-y-3 mt-5">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <div className="eyebrow mb-1.5">Workspace path to scan</div>
              <Input value={scanPath} onChange={(e) => setScanPath(e.target.value)} />
            </div>
            <Button onClick={() => scanWorkspace.mutate([scanPath])} loading={scanWorkspace.isPending}>
              <FolderSearch size={14} /> Scan now
            </Button>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <div className="eyebrow mb-1.5">Firebase Hosting inventory</div>
              <p className="font-body text-sm text-cream/65">Fan out across ARH/RRM/TSA/FTM/DRIJEZIE projects, refresh sites + custom domains.</p>
            </div>
            <Button variant="ghost" onClick={() => refreshHosting.mutate()} loading={refreshHosting.isPending}>
              <RefreshCw size={14} /> Refresh hosting
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardTitle>Build & version</CardTitle>
        <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
          <div>
            <div className="eyebrow mb-1">Version</div>
            <div className="font-mono text-gold-soft">v{APP_VERSION}</div>
          </div>
          <div>
            <div className="eyebrow mb-1">Commit</div>
            <div className="font-mono text-bronze">{GIT_SHA}</div>
          </div>
          <div>
            <div className="eyebrow mb-1">Built</div>
            <div className="font-mono text-cream/65 text-xs">{new Date(BUILD_TIME).toLocaleString()}</div>
          </div>
        </div>
      </Card>

      <Card>
        <CardTitle>Cross-links</CardTitle>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <ExternalLinkRow href="https://ascendantroadholdings.com/admin" label="ARH Admin Dashboard" />
          <ExternalLinkRow href="https://console.firebase.google.com/project/ascendant-road-holdings-llc" label="Firebase Console (ARH)" />
          <ExternalLinkRow href="https://github.com/reminiscentroadmediallc" label="GitHub org" />
          <ExternalLinkRow href="https://gamma.app/dashboard" label="Gamma dashboard" />
        </div>
      </Card>
    </div>
  )
}

function ExternalLinkRow({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 p-3 rounded-md border border-cream/10 bg-navy/30 hover:border-gold/40 hover:bg-navy/50 transition-all">
      <span className="font-body text-sm text-cream">{label}</span>
      <ExternalLink size={14} className="text-bronze" />
    </a>
  )
}
